import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { formatTranslation } from 'intl';
import { IntlShape } from 'react-intl';
import { DataGridColumnsFactory } from 'components/common/dataGrid/columnsFactory';
import { InspectionOnTableView } from 'presentation/inspection/inspectionOnView';
import { HashMap } from 'shared/types/commonView';
import { ISO6391LanguageCode } from 'shared/types/locale';
import { InspectionStatusSize } from '../Status/types';
import { MemoInspectionStatus } from '../Status';
import { MemoProcessIcon } from 'components/common/ProcessIcon';
import { Stack, Typography } from '@mui/material';
import { LabelledEntity } from 'shared/types/commonView';
import { ELLIPSIS_TEXT_SX } from 'components/common/dataGrid/styles';
import {
  inspectionRelatedIssuesCountValueGetter,
  documentStateValueGetter,
} from 'components/common/dataGrid/columnsFactory/valueGetters/index';
import { InspectionTranslationKeys } from 'intl/keys';
import { GridColumnFieldInspection } from 'components/common/dataGrid/columnsFactory/types';
import {
  getInspectionStateValueOptions,
} from 'components/common/dataGrid/columnsFactory/valueOptions';
import {
  getDocumentStateOperator,
} from 'components/common/dataGrid/columnsFactory/customFilters';
import {
  createDocumentStateCellRenderer,
} from 'components/common/dataGrid/columnsFactory/cellRenderers';

export class InspectionColumnsFactory extends DataGridColumnsFactory<InspectionOnTableView> {
  constructor(
    intl: IntlShape,
    timezone: string,
    private organizationId: string,
    private processes: HashMap<any>,
    private locale: ISO6391LanguageCode
  ) {
    super(intl, timezone);
  }

  private readonly renderProcessCell = (
    params: GridRenderCellParams<InspectionOnTableView>
  ) => (
    <Stack
      direction='row'
      alignItems='center'
      sx={{ height: '100%' }}
    >
      <MemoProcessIcon
        color={params.row.renderableProcess.color}
        code={params.row.renderableProcess.code}
      />
    </Stack>
  );

  private readonly renderInspectionStatusCell = (
    params: GridRenderCellParams<InspectionOnTableView>
  ) => (
    <Stack
      direction='row'
      alignItems='center'
      sx={{ height: '100%', minWidth: 0 }}
    >
      <MemoInspectionStatus
        status={params.row.status}
        size={InspectionStatusSize.table}
      />
    </Stack>
  );

  private readonly renderControlledPartyCell = (
    params: GridRenderCellParams<InspectionOnTableView, LabelledEntity[]>
  ) => {
    const items = params.value;
    const text = items ? items.map((item) => item.label).join(', ') : '';

    return (
      <Typography variant="body2" sx={ELLIPSIS_TEXT_SX}>
        {text}
      </Typography>
    );
  };

  private readonly renderWorkTypesCell = (
    params: GridRenderCellParams<InspectionOnTableView, LabelledEntity[]>
  ) => {
    const items = params.value;
    const text = items ? items.map((item) => item.label).join(', ') : '';

    return (
      <Typography variant="body2" sx={ELLIPSIS_TEXT_SX}>
        {text}
      </Typography>
    );
  };

  private inspectionState(): GridColDef<InspectionOnTableView> {
    const documentStateOperator = getDocumentStateOperator(this.intl);
    return {
      field: GridColumnFieldInspection.state,
      type: 'singleSelect',
      headerName: formatTranslation(
        this.intl,
        InspectionTranslationKeys.columnLabelState
      ),
      valueGetter: documentStateValueGetter,
      valueOptions: getInspectionStateValueOptions(this.intl),
      renderCell: createDocumentStateCellRenderer(),
      filterOperators: [documentStateOperator],
      width: 120,
    };
  }

  create(): GridColDef<InspectionOnTableView>[] {
    return [
      {
        ...this.orderNumber(),
        filterable: false,
      },
      {
        ...this.process(this.processes),
        renderCell: this.renderProcessCell,
      },
      this.inspectionNumber(),
      this.templateSummary(),
      {
        ...this.inspectionStatus(),
        renderCell: this.renderInspectionStatusCell,
      },
      this.inspectionState(),
      this.executionDate(this.locale),
      this.author(this.organizationId, this.intl),
      {
        ...this.controlledParty(),
        renderCell: this.renderControlledPartyCell,
      },
      {
        ...this.workTypes(),
        renderCell: this.renderWorkTypesCell,
      },
      {
        ...this.site(this.intl),
        headerName: formatTranslation(
          this.intl,
          InspectionTranslationKeys.columnLabelSite
        ),
        valueGetter: (_, row) => row.sites ?? [],
      },
      {
        ...this.level(this.intl),
        headerName: formatTranslation(
          this.intl,
          InspectionTranslationKeys.columnLabelLevel
        ),
      },
      {
        ...this.relatedIssuesCount(),
        valueGetter: inspectionRelatedIssuesCountValueGetter,
        headerName: formatTranslation(
          this.intl,
          InspectionTranslationKeys.relatedIssuesCount
        ),
      },
      this.inspectionResult(),
    ];
  }
}
